'use client';

import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { AIRTABLE_TABLES } from '@/lib/airtable/config';
// import { TableKey } from '@/lib/airtable/tables';

// Log TableKey values for SSR bug hunt
// console.log('[DirectoryContext] TableKey:', TableKey);
// console.log('[DirectoryContext] TableKey.SPATIAL_BIOLOGY_PRODUCTS:', TableKey.SPATIAL_BIOLOGY_PRODUCTS);
// console.log('[DirectoryContext] TableKey.CROS_AND_SERVICE_LABS:', TableKey.CROS_AND_SERVICE_LABS);
// console.log('[DirectoryContext] TableKey.SLIDE_SCANNING_INSTRUMENTS:', TableKey.SLIDE_SCANNING_INSTRUMENTS);
// console.log('[DirectoryContext] TableKey.ANALYSIS_SOFTWARE:', TableKey.ANALYSIS_SOFTWARE);
// console.log('[DirectoryContext] TableKey.BIOTECH_COMPANIES:', TableKey.BIOTECH_COMPANIES);
// console.log('[DirectoryContext] TableKey.PROVIDERS:', TableKey.PROVIDERS);

// Types for the Directory context
// Remove ViewMode type
type DirectoryContextType = {
  visibleColumns: Record<string, string[]>;
  setVisibleColumnsForTable: (tableName: string, columns: string[]) => void;
  columnOrder: Record<string, string[]>;
  setColumnOrderForTable: (tableName: string, order: string[]) => void;
  activeTable: string;
  setActiveTable: (table: string) => void;
  isColumnSelectorOpen: boolean;
  setIsColumnSelectorOpen: (isOpen: boolean) => void;
  currentPage: Record<string, number>;
  setCurrentPageForTable: (tableName: string, page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  // --- Filter state ---
  filters: Record<string, Record<string, any>>;
  setFiltersForTable: (tableName: string, filters: Record<string, any>) => void;
  clearFiltersForTable: (tableName: string) => void;
};

// Create the context with a default value
const DirectoryContext = createContext<DirectoryContextType | undefined>(undefined);

// Context provider component
export function DirectoryProvider({ children }: { children: React.ReactNode }) {
  // Remove viewMode state

  // State for visible columns by table
  const [visibleColumns, setVisibleColumns] = useState<Record<string, string[]>>({});
  const setVisibleColumnsForTable = useCallback((tableName: string, columns: string[]) => {
    setVisibleColumns(prev => ({
      ...prev,
      [tableName]: columns
    }));
  }, []);

  // State for column order by table
  const [columnOrder, setColumnOrder] = useState<Record<string, string[]>>({});
  const setColumnOrderForTable = useCallback((tableName: string, order: string[]) => {
    setColumnOrder(prev => ({
      ...prev,
      [tableName]: order
    }));
  }, []);

  // State for current page by table
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const setCurrentPageForTable = useCallback((tableName: string, page: number) => {
    setCurrentPage(prev => ({
      ...prev,
      [tableName]: page
    }));
  }, []);

  // Get the first logical key from AIRTABLE_TABLES
  const [activeTable, setActiveTable] = useState<string>('PROVIDERS');

  // State for itemsPerPage
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // State for isColumnSelectorOpen
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);

  // --- Filter state ---
  const [filters, setFilters] = useState<Record<string, Record<string, any>>>({});
  const setFiltersForTable = useCallback((tableName: string, newFilters: Record<string, any>) => {
    setFilters(prev => ({
      ...prev,
      [tableName]: newFilters
    }));
  }, []);
  const clearFiltersForTable = useCallback((tableName: string) => {
    setFilters(prev => {
      const updated = { ...prev };
      delete updated[tableName];
      return updated;
    });
  }, []);

  // Create context value with only viewMode, setViewMode, visibleColumns, setVisibleColumnsForTable
  const contextValue = useMemo(() => ({
    visibleColumns,
    setVisibleColumnsForTable,
    columnOrder,
    setColumnOrderForTable,
    activeTable,
    setActiveTable,
    isColumnSelectorOpen,
    setIsColumnSelectorOpen,
    currentPage,
    setCurrentPageForTable,
    itemsPerPage,
    setItemsPerPage,
    // --- Filters ---
    filters,
    setFiltersForTable,
    clearFiltersForTable,
  }), [
    visibleColumns, setVisibleColumnsForTable,
    columnOrder, setColumnOrderForTable,
    activeTable, setActiveTable,
    isColumnSelectorOpen, setIsColumnSelectorOpen,
    currentPage, setCurrentPageForTable,
    itemsPerPage, setItemsPerPage,
    filters, setFiltersForTable, clearFiltersForTable
  ]);

  return (
    <DirectoryContext.Provider value={contextValue}>
      {children}
    </DirectoryContext.Provider>
  );
}

// Hook to use the directory context
export function useDirectory() {
  const context = useContext(DirectoryContext);
  
  if (context === undefined) {
    throw new Error('useDirectory must be used within a DirectoryProvider');
  }
  
  return context;
} 
